import { gql } from '@apollo/client';
import getGraphQlClient from '~graphql/getGraphQlClient';
import { fetchNovita } from './novitaApi';
import { buildNovitaUnreadState } from './stateUtils';
import { NOVITA_UNREAD_PAGE_SIZE } from './config';

const NOVITA_USER_READS = gql`
  query NovitaUserReads($userId: ID!, $orgId: ID!) {
    novita_user_reads(
      where: {
        user_pointer: { have: { objectId: { equalTo: $userId } } }
        org_pointer: { have: { objectId: { equalTo: $orgId } } }
      }
      first: 1000
      order: [last_opened_at_DESC]
    ) {
      edges {
        node {
          id
          objectId
          novita_id
          novita_slug
          published_at
          first_opened_at
          last_opened_at
          open_count
          dedupe_key
        }
      }
    }
  }
`;

const NOVITA_USER_READ_BY_DEDUPE_KEY = gql`
  query NovitaUserReadByDedupeKey($dedupeKey: String!) {
    novita_user_reads(
      where: { dedupe_key: { equalTo: $dedupeKey } }
      first: 1
    ) {
      edges {
        node {
          id
          objectId
          novita_id
          novita_slug
          published_at
          first_opened_at
          last_opened_at
          open_count
          dedupe_key
        }
      }
    }
  }
`;

const CREATE_NOVITA_USER_READ = gql`
  mutation CreateNovitaUserRead($fields: CreateNovita_user_readFieldsInput!) {
    createNovita_user_read(input: { fields: $fields }) {
      novita_user_read {
        id
        objectId
        novita_id
        novita_slug
        published_at
        first_opened_at
        last_opened_at
        open_count
        dedupe_key
      }
    }
  }
`;

const UPDATE_NOVITA_USER_READ = gql`
  mutation UpdateNovitaUserRead(
    $id: ID!
    $fields: UpdateNovita_user_readFieldsInput!
  ) {
    updateNovita_user_read(input: { id: $id, fields: $fields }) {
      novita_user_read {
        id
        objectId
        novita_id
        novita_slug
        published_at
        first_opened_at
        last_opened_at
        open_count
        dedupe_key
      }
    }
  }
`;

const readNodesFromResult = (data) =>
  (data?.novita_user_reads?.edges || [])
    .map((edge) => edge?.node)
    .filter(Boolean);

const buildDedupeKey = (sessionIds, novitaId) =>
  `${sessionIds.orgId}:${sessionIds.userId}:${novitaId}`;

export const findNovitaUserReads = async (sessionIds) => {
  try {
    const client = await getGraphQlClient();
    const { data } = await client.query({
      query: NOVITA_USER_READS,
      variables: {
        userId: sessionIds.userId,
        orgId: sessionIds.orgId,
      },
      fetchPolicy: 'network-only',
    });

    return readNodesFromResult(data);
  } catch (error) {
    console.warn('[novita] user reads query failed', error);
    return [];
  }
};

const findReadByDedupeKey = async (dedupeKey) => {
  try {
    const client = await getGraphQlClient();
    const { data } = await client.query({
      query: NOVITA_USER_READ_BY_DEDUPE_KEY,
      variables: { dedupeKey },
      fetchPolicy: 'network-only',
    });

    return readNodesFromResult(data)[0] || null;
  } catch (error) {
    console.warn('[novita] read lookup failed', error);
    return null;
  }
};

const saveOpenedRead = async ({ sessionIds, input, existingRead }) => {
  const client = await getGraphQlClient();
  const now = new Date().toISOString();
  const commonFields = {
    novita_id: input.id,
    novita_slug: input.slug,
    published_at: input.publishedAt,
    last_opened_at: now,
    surface: input.surface,
    path: input.path,
    url: input.url,
  };

  if (existingRead) {
    const { data } = await client.mutate({
      mutation: UPDATE_NOVITA_USER_READ,
      variables: {
        id: existingRead.id,
        fields: {
          ...commonFields,
          open_count: (existingRead.open_count || 1) + 1,
        },
      },
    });

    return data?.updateNovita_user_read?.novita_user_read || null;
  }

  const { data } = await client.mutate({
    mutation: CREATE_NOVITA_USER_READ,
    variables: {
      fields: {
        ...commonFields,
        first_opened_at: now,
        open_count: 1,
        dedupe_key: buildDedupeKey(sessionIds, input.id),
        org_pointer: { link: sessionIds.orgId },
        user_pointer: { link: sessionIds.userId },
      },
    },
  });

  return data?.createNovita_user_read?.novita_user_read || null;
};

export const markNovitaOpened = async (sessionIds, input) => {
  const dedupeKey = buildDedupeKey(sessionIds, input.id);
  const [existingRead, readNodes] = await Promise.all([
    findReadByDedupeKey(dedupeKey),
    findNovitaUserReads(sessionIds),
  ]);
  const readItemIds = new Set(readNodes.map((node) => node.novita_id));

  try {
    const savedRead = await saveOpenedRead({
      sessionIds,
      input,
      existingRead,
    });
    readItemIds.add(savedRead?.novita_id || input.id);
  } catch (error) {
    console.warn('[novita] read save failed', error);
    readItemIds.add(input.id);
  }

  const response = await fetchNovita({ pageSize: NOVITA_UNREAD_PAGE_SIZE });
  return buildNovitaUnreadState({
    items: response.items,
    totalItems: response.pagination.totalItems,
    readItemIds,
    userCreatedAt: sessionIds.userCreatedAt,
  });
};

export const markNovitaItemsOpened = async (sessionIds, input) => {
  const uniqueIds = [...new Set((input.ids || []).filter(Boolean))];
  if (!uniqueIds.length) {
    const [readNodes, response] = await Promise.all([
      findNovitaUserReads(sessionIds),
      fetchNovita({ pageSize: NOVITA_UNREAD_PAGE_SIZE }),
    ]);
    return buildNovitaUnreadState({
      items: response.items,
      totalItems: response.pagination.totalItems,
      readItemIds: readNodes.map((node) => node.novita_id),
      userCreatedAt: sessionIds.userCreatedAt,
    });
  }

  const [readNodes, response] = await Promise.all([
    findNovitaUserReads(sessionIds),
    fetchNovita({ pageSize: NOVITA_UNREAD_PAGE_SIZE }),
  ]);
  const readItemIds = new Set(readNodes.map((node) => node.novita_id));
  const readByDedupeKey = new Map(
    readNodes.map((node) => [node.dedupe_key, node])
  );
  const itemsById = new Map(response.items.map((item) => [item.id, item]));

  for (const id of uniqueIds) {
    const item = itemsById.get(id);
    if (!item) continue;

    const dedupeKey = buildDedupeKey(sessionIds, id);
    if (readByDedupeKey.has(dedupeKey)) {
      readItemIds.add(id);
      continue;
    }

    try {
      const savedRead = await saveOpenedRead({
        sessionIds,
        existingRead: null,
        input: {
          id: item.id,
          slug: item.slug,
          publishedAt: item.publishedAt,
          surface: input.surface,
          path: input.path,
          url: input.url || item.url,
        },
      });
      readItemIds.add(savedRead?.novita_id || id);
    } catch (error) {
      console.warn('[novita] bulk read save failed', error);
      readItemIds.add(id);
    }
  }

  return buildNovitaUnreadState({
    items: response.items,
    totalItems: response.pagination.totalItems,
    readItemIds,
    userCreatedAt: sessionIds.userCreatedAt,
  });
};
